import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, Calendar, Rocket, Upload, Loader2, Users, Crown, Lock, Unlock, Clock, Trophy } from "lucide-react";
import api, { apiErr } from "../lib/api";
import Btn from "../components/Btn";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EMPTY = {
  title: "", subtitle: "", exam: "CPNS", package_id: "", scheduled_at: "", deadline_at: "",
  share_caption: "", poster_url: "", ig_post_url: "", tiktok_post_url: "", fb_post_url: "", active: true, auto_open: true,
};

export default function AdminMega() {
  const [events, setEvents] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [participants, setParticipants] = useState(null); // {event, rows} or null
  const [loading, setLoading] = useState(true);

  const load = () => Promise.all([
    api.get("/admin/mega").then(({ data }) => setEvents(data.events)),
    api.get("/admin/packages").then(({ data }) => setPackages(data.packages.filter((p) => p.active))),
  ]).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (e) => {
    if (!window.confirm(`Hapus event "${e.title}"?`)) return;
    try { await api.delete(`/admin/mega/${e.event_id}`); toast.success("Event dihapus"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const viewParticipants = async (e) => {
    setParticipants({ event: e, rows: null });
    try { const { data } = await api.get(`/admin/mega/${e.event_id}/participants`); setParticipants({ event: e, rows: data.participants }); }
    catch (err) { toast.error(apiErr(err)); setParticipants(null); }
  };

  const toggleOpen = async (e) => {
    const next = !e.is_open;
    if (next && !window.confirm(`Buka pengerjaan "${e.title}" sekarang? Semua siswa bertiket langsung bisa mengerjakan soal.`)) return;
    if (!next && !e.rewards_distributed && !window.confirm(`Tutup pengerjaan "${e.title}"? Ini akan MEMBAGIKAN HADIAH otomatis ke 10 besar (peringkat 1-3: VIP 1 bln + 100 token + 200 energi, dst). Hadiah hanya dibagikan sekali. Lanjutkan?`)) return;
    try {
      await api.put(`/admin/mega/${e.event_id}/open`, { is_open: next });
      toast.success(next ? "Tryout Akbar DIBUKA — siswa bisa mengerjakan 🚀" : "Tryout Akbar DITUTUP — hadiah 10 besar dibagikan 🏆");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  if (loading) return <div className="min-h-[30vh] grid place-items-center"><div className="w-10 h-10 rounded-full border-4 border-su-border border-t-su-primary animate-spin" /></div>;
  if (editing) return <MegaForm initial={editing === "new" ? EMPTY : editing} isNew={editing === "new"} packages={packages} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  return (
    <div className="space-y-5" data-testid="admin-mega">
      {participants && <ParticipantsModal data={participants} onClose={() => setParticipants(null)} />}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-black text-lg">Kelola Tryout Akbar</h2>
          <p className="text-su-muted font-medium text-sm">Tambah, jadwalkan, edit, atau hapus event akbar.</p>
        </div>
        <Btn data-testid="mega-new" onClick={() => setEditing("new")} disabled={packages.length === 0}><Plus className="w-4 h-4" /> Tambah Event</Btn>
      </div>
      {packages.length === 0 && <p className="text-sm text-su-danger">Buat minimal 1 tryout aktif dulu di tab Tryout sebelum membuat event akbar.</p>}
      <div className="grid md:grid-cols-2 gap-4">
        {events.map((e) => (
          <div key={e.event_id} className="card-chunky p-5" data-testid={`mega-card-${e.event_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[10px] font-heading font-black uppercase bg-su-danger/15 text-su-danger px-2 py-0.5 rounded-full"><Rocket className="w-3 h-3" />{e.exam}</span>
                  {!e.active && <span className="text-[10px] font-heading font-black bg-su-track text-su-muted px-2 py-0.5 rounded-full">NONAKTIF</span>}
                  <span data-testid={`mega-status-${e.event_id}`} className={`inline-flex items-center gap-1 text-[10px] font-heading font-black uppercase px-2 py-0.5 rounded-full ${e.is_open ? "bg-su-success/15 text-su-success" : "bg-su-energy/15 text-su-energy"}`}>
                    {e.is_open ? <><Unlock className="w-3 h-3" /> Dibuka</> : <><Lock className="w-3 h-3" /> Terkunci</>}
                  </span>
                  {e.auto_open !== false && <span className="inline-flex items-center gap-1 text-[10px] font-heading font-black uppercase bg-su-primary/15 text-su-primary px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" /> Auto</span>}
                </div>
                <h4 className="font-heading font-black text-lg mt-1">{e.title}</h4>
                <p className="text-xs text-su-muted">{e.subtitle}</p>
                <p className="text-xs text-su-muted mt-2 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Mulai: {new Date(e.scheduled_at).toLocaleString("id-ID")}</p>
                {e.deadline_at && <p className="text-xs text-su-muted mt-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Berakhir: {new Date(e.deadline_at).toLocaleString("id-ID")}</p>}
              </div>
              {e.poster_url && <img src={e.poster_url} alt="" className="w-16 h-16 rounded-xl object-cover border-2 border-su-border shrink-0" />}
              <div className="flex flex-col gap-1 shrink-0">
                <button data-testid={`mega-edit-${e.event_id}`} onClick={() => setEditing(e)} className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border hover:border-su-primary transition-colors"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`mega-delete-${e.event_id}`} onClick={() => remove(e)} className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border hover:border-su-danger text-su-danger transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <Btn variant="ghost" size="sm" data-testid={`mega-participants-${e.event_id}`} onClick={() => viewParticipants(e)} className="mt-3 w-full"><Users className="w-4 h-4" /> Lihat Peserta</Btn>
            <Btn variant={e.is_open ? "ghost" : "primary"} size="sm" data-testid={`mega-toggle-open-${e.event_id}`} onClick={() => toggleOpen(e)} className="mt-2 w-full">
              {e.is_open ? <><Lock className="w-4 h-4" /> Kunci Pengerjaan</> : <><Unlock className="w-4 h-4" /> Buka Pengerjaan</>}
            </Btn>
          </div>
        ))}
      </div>
    </div>
  );
}

function ParticipantsModal({ data, onClose }) {
  const { event, rows } = data;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="participants-modal">
      <div className="card-chunky p-6 max-w-xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-black text-lg flex items-center gap-2"><Users className="w-5 h-5 text-su-primary" /> Peserta Terdaftar</h3>
            <p className="text-sm text-su-muted font-medium">{event.title}</p>
          </div>
          <button onClick={onClose} data-testid="participants-close" className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border"><X className="w-4 h-4" /></button>
        </div>
        {rows === null ? (
          <div className="py-10 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-su-primary" /></div>
        ) : rows.length === 0 ? (
          <p className="text-su-muted font-medium text-center py-10">Belum ada peserta untuk event ini.</p>
        ) : (
          <div className="space-y-2" data-testid="participants-list">
            <p className="text-xs font-heading font-black uppercase text-su-muted">{rows.length} peserta</p>
            {rows.map((r, i) => (
              <div key={r.user_id + i} className="flex items-center gap-3 rounded-xl border-2 border-su-border p-3">
                <span className={`w-7 h-7 grid place-items-center rounded-lg font-heading font-black text-xs shrink-0 ${r.rank && r.rank <= 10 ? "bg-su-secondary/20 text-su-secondaryShadow" : "bg-su-track text-su-muted"}`}>{r.rank || "-"}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-extrabold text-sm truncate flex items-center gap-1">{r.name}{r.vip && <Crown className="w-3.5 h-3.5 text-su-secondary fill-current" />}</div>
                  <div className="text-xs text-su-muted truncate">{r.email}</div>
                  {r.reward_label && <div className="text-[11px] font-heading font-bold text-su-secondaryShadow mt-0.5 flex items-center gap-1"><Trophy className="w-3 h-3" /> {r.reward_label}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-[10px] font-heading font-black uppercase px-2 py-0.5 rounded-full ${r.status === "completed" ? "bg-su-success/15 text-su-success" : "bg-su-energy/15 text-su-energy"}`}>{r.status === "completed" ? "Selesai" : "Berlangsung"}</div>
                  {r.status === "completed" && <div className="font-mono font-bold text-su-primary text-sm mt-1">{r.score}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MegaForm({ initial, isNew, packages, onDone, onCancel }) {
  const [f, setF] = useState({ ...initial, scheduled_at: toLocalInput(initial.scheduled_at) || "", deadline_at: toLocalInput(initial.deadline_at) || "", package_id: initial.package_id || (packages[0]?.package_id || "") });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [posterPreview, setPosterPreview] = useState(initial.poster_url || "");
  const fileRef = useRef(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const uploadPoster = async (eventId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/admin/mega/${eventId}/poster`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.poster_url;
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPosterPreview(URL.createObjectURL(file));
    if (!isNew && initial.event_id) {
      setUploading(true);
      try { const url = await uploadPoster(initial.event_id, file); set("poster_url", url); toast.success("Poster diunggah"); }
      catch (err) { toast.error(apiErr(err)); }
      finally { setUploading(false); }
    } else {
      set("_pendingFile", file);
    }
  };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Judul wajib diisi"); return; }
    if (!f.package_id) { toast.error("Pilih paket tryout"); return; }
    if (!f.scheduled_at) { toast.error("Tentukan tanggal & jam"); return; }
    if (f.deadline_at && new Date(f.deadline_at) <= new Date(f.scheduled_at)) { toast.error("Batas waktu harus setelah jadwal mulai"); return; }
    const payload = {
      title: f.title, subtitle: f.subtitle, exam: f.exam, package_id: f.package_id,
      scheduled_at: new Date(f.scheduled_at).toISOString(),
      deadline_at: f.deadline_at ? new Date(f.deadline_at).toISOString() : "",
      share_caption: f.share_caption,
      poster_url: f.poster_url || "", active: f.active, auto_open: f.auto_open,
      ig_post_url: (f.ig_post_url || "").trim(), tiktok_post_url: (f.tiktok_post_url || "").trim(), fb_post_url: (f.fb_post_url || "").trim(),
    };
    setBusy(true);
    try {
      if (isNew) {
        const { data } = await api.post("/admin/mega", payload);
        if (f._pendingFile) { try { await uploadPoster(data.event_id, f._pendingFile); } catch {} }
      } else {
        await api.put(`/admin/mega/${initial.event_id}`, payload);
      }
      toast.success(isNew ? "Event ditambahkan!" : "Event diperbarui!");
      onDone();
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  const inputCls = "w-full bg-su-bg border-2 border-su-border rounded-xl px-4 py-2.5 font-medium focus:border-su-primary outline-none transition-colors";
  const lbl = "text-xs font-heading font-black uppercase text-su-muted mb-1.5 block";

  return (
    <div className="card-chunky p-6 max-w-2xl" data-testid="mega-form">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-black text-lg">{isNew ? "Tambah Tryout Akbar" : "Edit Tryout Akbar"}</h2>
        <button onClick={onCancel} data-testid="mega-cancel" className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-4">
        <div><label className={lbl}>Judul</label><input data-testid="megaf-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Tryout Akbar Nasional #1" className={inputCls} /></div>
        <div><label className={lbl}>Subjudul</label><input data-testid="megaf-subtitle" value={f.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Simulasi SKD serentak se-Indonesia" className={inputCls} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={lbl}>Jenis Ujian</label>
            <select data-testid="megaf-exam" value={f.exam} onChange={(e) => set("exam", e.target.value)} className={inputCls}>
              <option value="CPNS">CPNS</option><option value="SNBT">SNBT</option>
            </select>
          </div>
          <div><label className={lbl}>Paket Tryout</label>
            <select data-testid="megaf-package" value={f.package_id} onChange={(e) => set("package_id", e.target.value)} className={inputCls}>
              {packages.map((p) => {
                const optLabel = `${p.title} (${p.exam})`;
                return <option key={p.package_id} value={p.package_id}>{optLabel}</option>;
              })}
            </select>
          </div>
        </div>
        <div><label className={lbl}>Jadwal Mulai</label><input type="datetime-local" data-testid="megaf-schedule" value={f.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} className={inputCls} /></div>
        <div><label className={lbl}>Batas Waktu Pengerjaan (opsional)</label><input type="datetime-local" data-testid="megaf-deadline" value={f.deadline_at} onChange={(e) => set("deadline_at", e.target.value)} className={inputCls} /><p className="text-xs text-su-muted mt-1">Kosongkan bila tanpa batas. Saat waktu ini lewat, event otomatis ditutup & hadiah 1-10 dibagikan. Sesi yang sedang berjalan tetap bisa diselesaikan.</p></div>
        <div><label className={lbl}>Caption Berbagi</label><textarea data-testid="megaf-caption" value={f.share_caption} onChange={(e) => set("share_caption", e.target.value)} rows={2} placeholder="Aku ikut Tryout Akbar di SkorUp.io! 🔥 #SkorUp" className={inputCls} /></div>
        <div className="rounded-xl border-2 border-dashed border-su-border p-4 space-y-3">
          <p className="text-xs font-heading font-black uppercase text-su-muted">Link Postingan Sosmed (untuk di-repost siswa)</p>
          <p className="text-[11px] text-su-muted font-medium -mt-1">Tempel link postingan Tryout Akbar dari akun SkorUp.io. Siswa tinggal klik untuk membuka & repost ke Story.</p>
          <div><label className={lbl}>Link Postingan Instagram</label><input data-testid="megaf-ig-post" value={f.ig_post_url} onChange={(e) => set("ig_post_url", e.target.value)} placeholder="https://instagram.com/p/..." className={inputCls} /></div>
          <div><label className={lbl}>Link Postingan TikTok</label><input data-testid="megaf-tiktok-post" value={f.tiktok_post_url} onChange={(e) => set("tiktok_post_url", e.target.value)} placeholder="https://tiktok.com/@skorup.io/video/..." className={inputCls} /></div>
          <div><label className={lbl}>Link Postingan Facebook (opsional)</label><input data-testid="megaf-fb-post" value={f.fb_post_url} onChange={(e) => set("fb_post_url", e.target.value)} placeholder="https://facebook.com/..." className={inputCls} /></div>
        </div>
        <div>
          <label className={lbl}>Poster</label>
          <div className="flex items-center gap-3">
            {posterPreview ? <img src={posterPreview} alt="" className="w-24 h-24 rounded-xl object-cover border-2 border-su-border" /> : <div className="w-24 h-24 rounded-xl border-2 border-dashed border-su-border grid place-items-center text-su-muted text-xs">Belum ada</div>}
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="megaf-poster-input" onChange={onPickFile} />
              <Btn variant="ghost" size="sm" data-testid="megaf-poster-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Mengunggah...</> : <><Upload className="w-4 h-4" /> Pilih Poster</>}
              </Btn>
              <p className="text-[11px] text-su-muted mt-1">JPG/PNG, maks 5MB.{isNew ? " Poster diunggah setelah disimpan." : ""}</p>
            </div>
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="megaf-active" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="w-5 h-5 accent-[var(--su-primary)]" />
          <span className="font-heading font-bold text-sm">Aktif (tampil di halaman Akbar siswa)</span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" data-testid="megaf-auto-open" checked={f.auto_open} onChange={(e) => set("auto_open", e.target.checked)} className="w-5 h-5 mt-0.5 accent-[var(--su-primary)] shrink-0" />
          <span className="font-heading font-bold text-sm">Buka otomatis saat jadwal tiba<br /><span className="font-medium text-su-muted text-xs">Soal terbuka sendiri tepat di waktu "Jadwal Mulai" tanpa perlu klik manual. Siswa bertiket akan diberi notifikasi.</span></span>
        </label>
      </div>
      <div className="flex gap-3 mt-6">
        <Btn data-testid="megaf-save" onClick={save} disabled={busy} className="flex-1"><Save className="w-4 h-4" /> {busy ? "Menyimpan..." : "Simpan"}</Btn>
        <Btn variant="ghost" onClick={onCancel}>Batal</Btn>
      </div>
    </div>
  );
}
